import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, Search, Package, ArrowRight, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { getBranches } from '../../api/branches';
import { getBranchInventory } from '../../api/branchInventory';
import { createTransfer } from '../../api/stockTransfers';

const CreateTransferModal = ({ isOpen, onClose, onSuccess, defaultFromBranchId = null }) => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [fromBranchId, setFromBranchId] = useState(defaultFromBranchId || '');
  const [toBranchId, setToBranchId] = useState('');
  const [priority, setPriority] = useState('normal');
  const [notes, setNotes] = useState('');
  
  // Product selection
  const [inventory, setInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);

  // Fetch branches (with forTransfer to allow seeing all branches for stock transfers)
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await getBranches({ forTransfer: true });
        // Handle various API response formats: { success, data: [...] } or { branches: [...] } or [...]
        let branchesArray = [];
        if (Array.isArray(response)) {
          branchesArray = response;
        } else if (Array.isArray(response?.data)) {
          branchesArray = response.data;
        } else if (Array.isArray(response?.branches)) {
          branchesArray = response.branches;
        }
        setBranches(branchesArray);
      } catch (error) {
        console.error('Failed to load branches:', error);
        setBranches([]);
      }
    };
    if (isOpen) {
      fetchBranches();
    }
  }, [isOpen]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset selection and search when branch changes
  useEffect(() => {
    setSelectedItems([]);
    setSearchTerm('');
    setDebouncedSearchTerm('');
  }, [fromBranchId]);

  // Fetch inventory when from branch or search term changes
  useEffect(() => {
    const fetchInventory = async () => {
      if (!fromBranchId) {
        setInventory([]);
        return;
      }
      
      setLoadingInventory(true);
      try {
        const response = await getBranchInventory(fromBranchId, { 
          limit: 50,
          search: debouncedSearchTerm 
        });
        
        // Handle response format: { inventory: [...] } or { data: { inventory: [...] } }
        let inventoryArray = [];
        if (Array.isArray(response?.inventory)) {
          inventoryArray = response.inventory;
        } else if (Array.isArray(response?.data?.inventory)) {
          inventoryArray = response.data.inventory;
        } else if (Array.isArray(response)) {
          inventoryArray = response;
        }
        setInventory(inventoryArray);
      } catch (error) {
        console.error('Failed to load inventory:', error);
        toast.error('Failed to load source branch inventory');
        setInventory([]);
      } finally {
        setLoadingInventory(false);
      }
    };
    
    fetchInventory();
  }, [fromBranchId, debouncedSearchTerm]);

  // Add item to selection
  const addItem = (item) => {
    if (selectedItems.find(s => s.product_id === item.product_id)) {
      toast.info('Product already added');
      return;
    }
    setSelectedItems(prev => [...prev, {
      product_id: item.product_id,
      product_name: item.product_name,
      product_code: item.product_code,
      available_stock: item.stock_quantity,
      quantity: 1
    }]);
  };

  // Update item quantity
  const updateQuantity = (productId, quantity) => {
    setSelectedItems(prev => prev.map(item => 
      item.product_id === productId 
        ? { ...item, quantity: Math.min(Math.max(1, quantity), item.available_stock) }
        : item
    ));
  };

  // Remove item
  const removeItem = (productId) => {
    setSelectedItems(prev => prev.filter(item => item.product_id !== productId));
  };

  // Submit transfer
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!fromBranchId || !toBranchId) {
      toast.error('Please select source and destination branches');
      return;
    }
    
    if (fromBranchId === toBranchId) {
      toast.error('Source and destination cannot be the same branch');
      return;
    }
    
    if (selectedItems.length === 0) {
      toast.error('Please add at least one product to transfer');
      return;
    }

    setSubmitting(true);
    try {
      const transfer = await createTransfer({
        fromBranchId: parseInt(fromBranchId),
        toBranchId: parseInt(toBranchId),
        priority,
        notes,
        items: selectedItems.map(item => ({
          productId: item.product_id,
          quantity: item.quantity
        }))
      });
      
      toast.success(`Transfer ${transfer.transfer_number} created successfully`);
      onSuccess?.(transfer);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create transfer');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const totalItems = selectedItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-primary-black text-white">
          <h2 className="text-xl font-bold">Create Stock Transfer</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
          {/* Branch Selection */}
          <div className="p-6 border-b bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium mb-1">From Branch *</label>
                <select
                  value={fromBranchId}
                  onChange={(e) => setFromBranchId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
                  required
                >
                  <option value="">Select source...</option>
                  {Array.isArray(branches) && branches.map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="hidden md:flex justify-center">
                <ArrowRight className="w-8 h-8 text-gray-400" />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">To Branch *</label>
                <select
                  value={toBranchId}
                  onChange={(e) => setToBranchId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
                  required
                >
                  <option value="">Select destination...</option>
                  {Array.isArray(branches) && branches.filter(b => b.id.toString() !== fromBranchId).map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-1">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold"
                  placeholder="Reason for transfer..."
                />
              </div>
            </div>
          </div>

          {/* Product Selection Area */}
          <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
            {/* Available Products */}
            <div className="flex-1 border-r overflow-hidden flex flex-col">
              <div className="p-4 border-b">
                <h3 className="font-semibold mb-2">Available Products</h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                    disabled={!fromBranchId}
                  />
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                {!fromBranchId ? (
                  <p className="text-gray-500 text-center py-8">Select source branch to view inventory</p>
                ) : loadingInventory ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-accent-gold border-t-transparent rounded-full"></div>
                  </div>
                ) : inventory.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No products found</p>
                ) : (
                  <div className="space-y-2">
                    {inventory.map(item => (
                      <div 
                        key={item.product_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                        onClick={() => addItem(item)}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.product_code}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            item.stock_quantity <= 0 ? 'bg-red-100 text-red-700' :
                            item.is_low_stock ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {item.stock_quantity} in stock
                          </span>
                          <Plus className="w-4 h-4 text-accent-gold" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Selected Items */}
            <div className="flex-1 overflow-hidden flex flex-col bg-gray-50">
              <div className="p-4 border-b bg-white">
                <h3 className="font-semibold">Transfer Items ({selectedItems.length})</h3>
                <p className="text-sm text-gray-500">Total: {totalItems} units</p>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                {selectedItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Click products to add them</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedItems.map(item => (
                      <div key={item.product_id} className="bg-white p-3 rounded-lg border">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{item.product_name}</p>
                            <p className="text-xs text-gray-500">Available: {item.available_stock}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(item.product_id)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                            className="p-1 border rounded hover:bg-gray-100"
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item.product_id, parseInt(e.target.value) || 1)}
                            className="w-20 px-2 py-1 border rounded text-center"
                            min="1"
                            max={item.available_stock}
                          />
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                            className="p-1 border rounded hover:bg-gray-100"
                            disabled={item.quantity >= item.available_stock}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || selectedItems.length === 0}
              className="px-6 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
            >
              {submitting ? 'Creating...' : `Create Transfer (${totalItems} items)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTransferModal;
